import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Heart, Users, Sparkles, Home } from 'lucide-react';
import aboutImage from '../../assets/about.jpg';

export const AboutBlock = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section ref={ref} className="relative min-h-screen py-24 px-6 bg-gradient-to-br from-surface via-white to-secondary-100 overflow-hidden">
      {/* Background decoration */}
      <motion.div
        className="absolute top-20 left-10 w-72 h-72 bg-brand-400/20 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-32 right-16 w-96 h-96 bg-secondary-500/20 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16 flex justify-center"
        >
          <div className="relative w-full max-w-4xl">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-400 to-secondary-500 rounded-3xl blur-2xl opacity-20" />
            <img
              src={aboutImage}
              alt="המקומית - הקהילה שלנו"
              className="relative w-full h-auto rounded-3xl shadow-2xl border-4 border-white/50"
            />
          </div>
        </motion.div>

        {/* Opening - Personal Hook */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-l from-brand-700 via-brand-600 to-brand-700 leading-tight mb-8">
            אודות המקומית
          </h1>
        </motion.div>

        {/* Main Story Content */}
        <div className="max-w-4xl mx-auto space-y-8 mb-16">
          {/* Opening paragraph */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 shadow-xl border border-brand-400/20"
          >
            <p className="text-xl md:text-2xl text-brand-700 leading-relaxed font-medium">
              היי, אני עודד נפתלי ממושב עופר. בן זוג ליעלי, אבא לעמליה ויערה, ואדם שמאמין שקהילה חזקה מתחילה בחיבור אמיתי בין שכנים.
            </p>
          </motion.div>

          {/* The Problem */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 shadow-xl border border-brand-400/20"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl blur-md opacity-30" />
                <div className="relative bg-gradient-to-br from-secondary-500 to-secondary-600 p-3 rounded-xl">
                  <Heart className="w-6 h-6 text-white" strokeWidth={2} />
                </div>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-700">
                מה שראיתי סביבי
              </h2>
            </div>
            <p className="text-lg md:text-xl text-brand-700 leading-relaxed mb-4">
              במשך שנים, אני חי באזור שמורכב מקהילות קטנות – כולן מלאות באנשים נפלאים, בעלי יכולות מופלאות והמון רצון טוב. ראיתי מסביבי יוזמות מדהימות ועסקים קטנים שקמו בתקווה גדולה, אבל נפלו לא בגלל שלא היו טובים מספיק.
            </p>
            <p className="text-lg md:text-xl text-brand-700 leading-relaxed font-semibold">
              הם נפלו כי היה לנו קשה לדעת אחד על השני. וגם כשידענו – היה מסורבל לחבר, לפגוש, לצרוך.
            </p>
          </motion.div>

          {/* The Vision */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="bg-gradient-to-br from-brand-400 to-brand-500 rounded-2xl p-8 md:p-10 shadow-2xl text-white"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-white rounded-xl blur-md opacity-30" />
                <div className="relative bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                  <Sparkles className="w-6 h-6 text-white" fill="currentColor" strokeWidth={2} />
                </div>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">
                למה הקמתי את המקומית
              </h2>
            </div>
            <p className="text-lg md:text-xl leading-relaxed mb-4">
              הקמתי את המקומית בדיוק בשביל זה – כדי לסגור את הפער הזה. כדי שנדע מי הם האנשים שחיים לידנו, מה הם עושים, ואיך אנחנו יכולים לתמוך אחד בשני בצורה פשוטה, ישירה ואמיתית.
            </p>
            <p className="text-lg md:text-xl leading-relaxed font-semibold">
              אני חולם על קהילות שמכירות אחת את השנייה. קהילות שחוגגות את העשייה של השכנים שלהן, צורכות מקומי, ועל ידי כך בונות כלכלה מקומית חזקה ותרבות ייחודית משלהן.
            </p>
          </motion.div>

          {/* The Future We're Building */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 shadow-xl border border-brand-400/20"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-400 to-brand-500 rounded-xl blur-md opacity-30" />
                <div className="relative bg-gradient-to-br from-brand-400 to-brand-500 p-3 rounded-xl">
                  <Users className="w-6 h-6 text-white" strokeWidth={2} />
                </div>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-700">
                איזה עתיד אנחנו בונים ביחד
              </h2>
            </div>
            <p className="text-lg md:text-xl text-brand-700 leading-relaxed mb-4">
              אני רואה לפניי עתיד שבו הקהילה שלך היא לא רק מקום שאתה גר בו – אלא מקום שאתה חי בו באמת. מקום שבו אתה מכיר את השכנים שלך בשמם, יודע למי לפנות כשאתה צריך עזרה, ויודע למי להציע עזרה כשמישהו אחר צריך.
            </p>
            <p className="text-lg md:text-xl text-brand-700 leading-relaxed">
              עתיד שבו כלכלה מקומית חזקה תומכת ביוזמות הקטנות, בעסקים המקומיים, ובאנשים שיוצרים ערך אמיתי לסביבה שלהם. קהילות שמבוססות על אמון, על שותפות, ועל הידיעה שכשאנחנו מחזקים אחד את השני – כולנו מנצחים.
            </p>
          </motion.div>

          {/* Manifesto-style highlight */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="relative bg-gradient-to-r from-secondary-500/10 via-brand-400/10 to-secondary-500/10 rounded-2xl p-8 md:p-12 border-2 border-brand-400/30"
          >
            <div className="absolute top-0 left-0 right-0 flex justify-center -mt-6">
              <div className="bg-gradient-to-r from-brand-400 to-secondary-500 px-6 py-2 rounded-full">
                <Sparkles className="w-5 h-5 text-white inline-block" fill="currentColor" />
              </div>
            </div>
            <p className="text-xl md:text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-l from-brand-700 via-brand-600 to-brand-700 leading-relaxed">
              "כשאנחנו תומכים אחד בשני, אנחנו לא רק קונים שירות או מוצר –<br />אנחנו בונים קהילה שבה כולנו שייכים."
            </p>
          </motion.div>
        </div>

        {/* Call to Action - Invitation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600 rounded-3xl p-10 md:p-16 shadow-2xl">
            <div className="mb-8 flex justify-center">
              <div className="relative">
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute inset-0 bg-white rounded-full blur-xl opacity-30"
                />
                <div className="relative bg-white/20 backdrop-blur-sm p-5 rounded-full">
                  <Home className="w-12 h-12 md:w-16 md:h-16 text-white" strokeWidth={1.5} />
                </div>
              </div>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
              אני מזמין אתכם להצטרף
            </h2>
            <p className="text-xl md:text-2xl text-white/95 leading-relaxed mb-8">
              להכיר את הקהילות שלכם, לחזק אותן, ובעיקר – להיות חלק מהן.
            </p>

            <div className="inline-block">
              <p className="text-2xl md:text-3xl lg:text-4xl font-black text-white leading-relaxed">
                בואו.
                <br />
                הקהילות שלכם מחכות לכם.
              </p>
            </div>

            {/* Signature */}
            <div className="mt-12 pt-8 border-t border-white/30">
              <p className="text-lg md:text-xl text-white/90 font-medium">
                – עודד נפתלי
              </p>
              <p className="text-base md:text-lg text-white/70 mt-2">
                מושב עופר
              </p>
            </div>
          </div>
        </motion.div>

        {/* Bottom decorative quote */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1 }}
          className="mt-16 text-center"
        >
          <p className="text-base md:text-lg text-brand-600 font-medium italic">
            כי קהילה חזקה מתחילה בהכרות. ומסתיימת בשייכות.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
